/**
 * This service handles all the external data pulling for the sidebar (mainly the menu tree).
 */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { MenuItem } from 'primeng/api';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { MenuObject } from '../../../../core/sidebar/sidebar.interface';
import { AuthService } from '../../../../auth/auth.service';

@Injectable()
export class SidebarService {
  private menuTreeItems$ = new BehaviorSubject<MenuItem[]>([]);
  readonly menuTreeItemsChanges = this.menuTreeItems$.asObservable();
  public menuObjectsMap = new Map<number, MenuObject>();

  public httpOptions: any;
  private showHideSidebarSource: Subject<boolean> = new Subject();
  readonly menuEndpoint = '/api/sys/menus';
  readonly menuCommonDataEndpoint = '/api/sys/menus/commondata';

  constructor(private http: HttpClient, private authService: AuthService) {
    const currentUser = sessionStorage.getItem('TOKEN');
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + currentUser
      })
    };
  }

  public onShowHideSidebar(): Observable<boolean> {
    return this.showHideSidebarSource.asObservable();
  }

  public notifyShowHideSidebar(visible: boolean): void {
    this.showHideSidebarSource.next(visible);
  }

  public hasAccessToMenuObject(menuObjectId: number): boolean {
    const menuObject = this.menuObjectsMap.get(menuObjectId);
    const userClassId = this.authService.getClassId();
    if (!menuObject || !userClassId) {
      return false;
    }
    return menuObject.userClassIds.includes(userClassId);
  }

  /**
   * get menu tree items based on given user (inferred by server).
   * If no attempt was made, this function will create a global promise
   * for the menu tree array, and return it.
   */
  fetchMenuTreeItems(menuId: number, systemMenuId: number): Observable<MenuItem[]> {
    if (!this.menuTreeItems$.getValue().length) {
      let params = new HttpParams();
      // Begin assigning parameters
      params = params.append('menuId', menuId ? menuId.toString() : '');
      params = params.append('systemMenuId', systemMenuId ? systemMenuId.toString() : '');
      this.httpOptions.params = params;
      return this.fetchMenusCommonData().pipe(
        switchMap(() =>
          this.http.get(`${environment.systemAPI}${this.menuEndpoint}`, this.httpOptions).pipe(
            tap((response: any) => this.menuTreeItems$.next(response))
          )
        ),
        // Never let a failed menu/common-data call hang the route guard (stuck loader).
        // Fall back to an empty menu so callers can react (e.g. show "no menu" message).
        catchError(() => {
          this.menuTreeItems$.next([]);
          return of([] as MenuItem[]);
        })
      );
    }
    return this.menuTreeItems$;
  }

  fetchMenusCommonData(): Observable<any> {
    return this.http.post(`${environment.systemAPI}${this.menuCommonDataEndpoint}`, {}).pipe(
      tap((res: any) => {
        try {
          const menuObjectInUserClassList: MenuObject[] = (JSON.parse((res.list || [])[0]?.menuObjectInUserClassList || null) || []).map(
            (item: any) =>
              ({
                menuObjectId: item.menuObjectId,
                siteId: item.siteId,
                userClassIds: (item.userClassId || '').split(',').map(Number)
              }) as MenuObject
          );
          for (const menuObject of menuObjectInUserClassList) {
            this.menuObjectsMap.set(menuObject.menuObjectId, menuObject);
          }
        } catch (e) {
        }
      })
    );
  }
}
